/**
 * Parametric circuit generator — same seed + same params = same circuit, always
 * (mulberry32 PRNG, deterministic retry loop). Runs identically on the client
 * (instant preview, track rebuild on room join) and on the server (authority).
 *
 * Algorithm (SPEC §8): control points spread around a ring with seeded radial
 * variations (driven by the corner count) → closed centripetal Catmull-Rom →
 * finely resampled centerline → width varying continuously between min and max
 * → validity check (clearance, curvature, width) with automatic retries.
 * Surfaces are assigned as contiguous seeded zones (SPEC addendum), the zone
 * containing the start line always being asphalt.
 */

import type { SurfaceId } from './physics';
import { SURFACE_IDS } from './physics';
import type { Track, TrackControlPoint } from './track';
import { buildTrack } from './track';
import { validateTrack } from './validation';

export interface CircuitParams {
  /** Number of corners (control points around the ring). */
  corners: number;
  /** Narrowest corridor (full width, px). */
  minWidth: number;
  /** Widest corridor (full width, px). */
  maxWidth: number;
  /** Approximate centerline length (px). */
  approxLength: number;
  /** Random seed (uint32). */
  seed: number;
}

export const CIRCUIT_PARAM_BOUNDS = {
  corners: { min: 4, max: 14 },
  minWidth: { min: 90, max: 240 },
  maxWidth: { min: 90, max: 240 },
  approxLength: { min: 2500, max: 12000 },
  seed: { min: 0, max: 0xffffffff },
} as const;

export function clampCircuitParams(p: CircuitParams): CircuitParams {
  const clampInt = (v: number, b: { min: number; max: number }): number =>
    Math.round(Math.min(Math.max(Number.isFinite(v) ? v : b.min, b.min), b.max));
  const corners = clampInt(p.corners, CIRCUIT_PARAM_BOUNDS.corners);
  let minWidth = clampInt(p.minWidth, CIRCUIT_PARAM_BOUNDS.minWidth);
  let maxWidth = clampInt(p.maxWidth, CIRCUIT_PARAM_BOUNDS.maxWidth);
  if (minWidth > maxWidth) [minWidth, maxWidth] = [maxWidth, minWidth];
  return {
    corners,
    minWidth,
    maxWidth,
    approxLength: clampInt(p.approxLength, CIRCUIT_PARAM_BOUNDS.approxLength),
    seed: clampInt(p.seed, CIRCUIT_PARAM_BOUNDS.seed) >>> 0,
  };
}

/** mulberry32 — tiny deterministic PRNG, identical on every JS engine. */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface GeneratedCircuit {
  track: Track;
  controlPoints: TrackControlPoint[];
  /** Retries needed before a valid layout came out (diagnostic). */
  attempts: number;
}

const MAX_ATTEMPTS = 60;

/**
 * Generates a valid circuit, or null if the parameter combination cannot
 * produce one within MAX_ATTEMPTS (rare — very tight length/width combos).
 */
export function generateCircuit(rawParams: CircuitParams): GeneratedCircuit | null {
  const params = clampCircuitParams(rawParams);
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    // Deterministic retry chain: attempt N of a given seed is always the same.
    const rng = mulberry32((params.seed + Math.imul(attempt, 2654435761)) >>> 0);
    const controlPoints = layoutControlPoints(params, rng, attempt);
    const track = buildTrack(controlPoints, { startOffset: 0 });
    if (validateTrack(track).valid && lengthAcceptable(track.length, params.approxLength)) {
      return { track, controlPoints, attempts: attempt };
    }
  }
  return null;
}

function lengthAcceptable(actual: number, target: number): boolean {
  return actual > target * 0.6 && actual < target * 1.6;
}

function layoutControlPoints(
  params: CircuitParams,
  rng: () => number,
  attempt: number,
): TrackControlPoint[] {
  const n = params.corners;
  // Ring radius from the target circumference; radial noise stretches the path,
  // so undershoot slightly. Later attempts shrink the variation for validity.
  const variation = Math.max(0.12, 0.38 - attempt * 0.005);
  const baseRadius = params.approxLength / (2 * Math.PI * (1 + variation * 0.45));
  const angleJitter = ((2 * Math.PI) / n) * 0.28;

  const points: TrackControlPoint[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * 2 * Math.PI + (rng() * 2 - 1) * angleJitter;
    const radius = baseRadius * (1 + (rng() * 2 - 1) * variation);
    const width = params.minWidth + rng() * (params.maxWidth - params.minWidth);
    points.push({
      x: Math.round(baseRadius * 1.6 + Math.cos(angle) * radius),
      y: Math.round(baseRadius * 1.6 + Math.sin(angle) * radius),
      width: Math.round(width),
      surface: 'asphalt',
    });
  }

  assignSurfaceZones(points, rng);
  return points;
}

/**
 * Contiguous surface zones (2 to 5 depending on the corner count), no two
 * adjacent zones with the same surface, and the start segment always asphalt.
 */
function assignSurfaceZones(points: TrackControlPoint[], rng: () => number): void {
  const n = points.length;
  const zoneCount = Math.min(2 + Math.floor(rng() * 4), Math.max(2, Math.floor(n / 2)));

  // Zone boundaries: distinct cut positions around the ring, keeping index 0
  // inside the first zone (which is forced to asphalt for a fair start).
  const cuts = new Set<number>();
  while (cuts.size < zoneCount - 1) {
    cuts.add(1 + Math.floor(rng() * (n - 1)));
  }
  const sortedCuts = [...cuts].sort((a, b) => a - b);

  const zoneOf = (index: number): number => {
    let zone = 0;
    for (const cut of sortedCuts) {
      if (index >= cut) zone++;
    }
    return zone;
  };

  const zoneSurfaces: SurfaceId[] = [];
  for (let z = 0; z < zoneCount; z++) {
    if (z === 0) {
      zoneSurfaces.push('asphalt');
      continue;
    }
    let pick: SurfaceId;
    do {
      pick = SURFACE_IDS[Math.floor(rng() * SURFACE_IDS.length)] as SurfaceId;
    } while (pick === zoneSurfaces[z - 1] || (z === zoneCount - 1 && pick === 'asphalt'));
    zoneSurfaces.push(pick);
  }

  for (let i = 0; i < n; i++) {
    (points[i] as TrackControlPoint).surface = zoneSurfaces[zoneOf(i)] as SurfaceId;
  }
}

/**
 * Checkpoint curvilinear positions: 8 to 16 invisible gates evenly spread along
 * the centerline (SPEC §8), excluding s = 0 which is the start/finish line.
 */
export function computeCheckpoints(track: Track): number[] {
  const count = Math.min(16, Math.max(8, Math.round(track.length / 550)));
  const checkpoints: number[] = [];
  for (let i = 1; i <= count; i++) {
    checkpoints.push((track.length * i) / (count + 1));
  }
  return checkpoints;
}
